import { Link } from 'react-router-dom';
import useDocumentTitle from '../hooks/useDocumentTitle';

function Terms() {
  useDocumentTitle('Terms of Service — MMI');

  return (
    <main className="legal-page">
      <article className="legal-document">
        <header className="legal-header">
          <p className="legal-updated">Last updated — May 2026</p>
          <h1>Terms of Service</h1>
          <p className="legal-intro">
            These terms govern your use of MMI&apos;s website and the professional services we deliver.
            By engaging with us — whether through a proposal, statement of work, or ongoing partnership
            — you agree to the framework below. Plain language where possible; legal clarity where
            required.
          </p>
        </header>

        <section className="legal-section">
          <h2>Who we are</h2>
          <p>
            MMI (M &amp; M Innovators) is a technology studio specializing in Agentic AI systems,
            AR/VR and immersive experiences, Meta applications, and intelligent automation. We operate
            from Pakistan and serve clients globally.
          </p>
        </section>

        <section className="legal-section">
          <h2>Services covered</h2>
          <p>Our engagements typically include one or more of:</p>
          <ul>
            <li>
              <strong>Agentic AI</strong> — autonomous agents, workflow
              automation, LLM integrations, and decision systems that act beyond simple chat
            </li>
            <li>
              <strong>AR/VR</strong> — simulations, training environments,
              interactive 3D, and spatial experiences
            </li>
            <li>
              <strong>Meta Applications</strong> — virtual worlds,
              digital twins, AI-powered avatars, and persistent immersive platforms
            </li>
            <li>
              <strong>Automation &amp; integration</strong> — connecting
              AI and immersive systems to your existing software stack
            </li>
          </ul>
          <p>
            Specific deliverables, milestones, and fees are defined in project agreements — not
            inferred from this page alone.
          </p>
        </section>

        <section className="legal-section">
          <h2>Client responsibilities</h2>
          <p>To keep projects moving, clients agree to:</p>
          <ul>
            <li>Provide timely feedback, assets, and access required for integration</li>
            <li>Ensure they have rights to any content, data, or IP they supply to us</li>
            <li>Designate a point of contact for decisions that affect scope or timeline</li>
            <li>Use delivered systems lawfully and in compliance with applicable regulations</li>
            <li>Not attempt to reverse-engineer, resell, or misrepresent MMI work without permission</li>
          </ul>
          <p>
            Delays caused by missing inputs may shift timelines. We will communicate early when
            blockers appear.
          </p>
        </section>

        <section className="legal-section">
          <h2>Intellectual property</h2>
          <p>
            <strong>Your data stays yours.</strong> Client-provided
            content, business data, and proprietary materials remain client property.
          </p>
          <p>
            <strong>Deliverables</strong> — upon full payment, clients
            receive the rights agreed in the statement of work (typically ownership or license to
            custom work product built specifically for them).
          </p>
          <p>
            <strong>MMI retains</strong> rights to pre-existing tools,
            frameworks, agent architectures, reusable components, and general know-how developed
            before or outside the specific engagement. We may reuse non-confidential patterns across
            projects unless restricted by contract.
          </p>
          <p>
            AI models, prompts, and agent configurations may involve third-party licenses (e.g. OpenAI,
            Anthropic). Clients are responsible for their own API usage and compliance with those
            providers&apos; terms.
          </p>
        </section>

        <section className="legal-section">
          <h2>Project timelines and scope</h2>
          <p>
            Estimates are based on information available at kickoff. Scope changes — new features,
            additional integrations, expanded agent capabilities — may require a change order and
            revised timeline. We prefer clarity over surprise invoices.
          </p>
          <p>
            Our six-phase process (Discovery through Deploy and Stay) defines how we structure work.
            Formal schedules are documented per project.
          </p>
        </section>

        <section className="legal-section">
          <h2>Payment</h2>
          <p>
            Payment terms are specified in proposals and contracts. Unless otherwise agreed, invoices
            are due per milestone or monthly retainer schedule. Late payments may pause work until
            accounts are current.
          </p>
        </section>

        <section className="legal-section">
          <h2>Limitation of liability</h2>
          <p>
            MMI provides services on a best-effort professional basis. AI and immersive systems can
            behave unpredictably in edge cases. To the fullest extent permitted by law:
          </p>
          <ul>
            <li>
              We are not liable for indirect, incidental, or consequential damages including lost
              profits or data loss beyond our control
            </li>
            <li>
              Our total liability for any claim related to a project is limited to the fees paid for
              that specific engagement in the twelve months preceding the claim
            </li>
            <li>
              We do not guarantee uninterrupted uptime of third-party APIs, cloud platforms, or
              model providers
            </li>
          </ul>
          <p>
            Nothing here limits liability where law does not allow it.
          </p>
        </section>

        <section className="legal-section">
          <h2>Website use</h2>
          <p>
            This site is provided for information about MMI. Do not misuse it — no scraping that
            degrades performance, no attempts to breach security, no unlawful content submission
            through our forms.
          </p>
        </section>

        <section className="legal-section">
          <h2>Governing law</h2>
          <p>
            These terms are governed by the laws of <strong>Pakistan</strong>.
            Disputes will be handled in courts or arbitration forums within Pakistan unless both
            parties agree otherwise in writing.
          </p>
        </section>

        <section className="legal-section">
          <h2>Changes</h2>
          <p>
            We may revise these terms as our services evolve. Continued use of the site or new
            engagements after updates constitutes acceptance of the revised terms.
          </p>
        </section>

        <div className="legal-contact">
          <p>
            Questions about these terms? Email{' '}
            <a href="mailto:hello@mmi.com">hello@mmi.com</a> or{' '}
            <Link to="/contact">get in touch</Link>.
          </p>
        </div>
      </article>
    </main>
  );
}

export default Terms;
